<!-- One template for the three methods; config says what each has. A signed method (Join/Update,
     Access/Update) has the key selector, the Security section, How to sign and the code; SSO has
     the project's Influencer SSO settings instead, as the sign-in provider proves who the person
     is. Join/Update and SSO add the campaign targeting rows. Info lines use the design system's
     field description class. -->
<div class="influencer-auto-join-tab" [class.influencer-auto-join-tab--locked]="locked">
  <div class="mb-4">
    <app-sb-input-checkbox
      mode="switch"
      [label]="config.enableLabel"
      [description]="enableDescription"
      [ngModel]="methodEnabled"
      (ngModelChange)="methodEnabled = $event"
    ></app-sb-input-checkbox>
  </div>

  <div class="sb-field__description mb-3">{{config.intro}}</div>

  @if (method === 'sso') {
    <h5 class="mt-4 mb-2">How SSO works</h5>
    <ol class="text-muted mb-4">
      @for (step of ssoSteps; track step) {
      <li>{{step}}</li>
      }
    </ol>

    <h5 class="mb-2">Influencer SSO settings</h5>
    @if (ssoState === 'ready') {
    <div class="table-responsive">
      <table class="table table-sm access-sso-settings">
        <thead>
          <tr>
            <th scope="col">Name</th>
            <th scope="col">Protocol</th>
            <th scope="col">Status</th>
          </tr>
        </thead>
        <tbody>
          @for (setting of settings; track setting) {
          <tr>
            <td>{{setting.name}}</td>
            <td>{{protocolLabel(setting)}}</td>
            <td>{{setting.enabled ? 'Enabled' : 'Disabled'}}</td>
          </tr>
          }
        </tbody>
      </table>
    </div>
    }
    @if (ssoState === 'none') {
    <div class="alert alert-info">{{ssoNone}}</div>
    }
    @if (ssoWarning) {
    <div class="alert alert-warning">{{ssoWarning}}</div>
    }
    @if (ssoButtonLabel) {
    <app-sb-button
      variant="outline"
      icon="fa-solid fa-id-card"
      routerLink="/influencer-sso"
      [label]="ssoButtonLabel"
      [queryParams]="ssoListQuery"
    ></app-sb-button>
    }
  }

  @if (linkShown) {
    @if (method === 'sso') {
    <div class="row mt-4">
      <div class="col-lg-6">
        <app-sb-input-select
          label="SSO setting"
          placeholder="Select an SSO setting"
          [clearable]="false"
          [disabled]="locked"
          [items]="settingOptions"
          [ngModel]="selectedSettingId"
          (ngModelChange)="onSettingSelected($event)"
        ></app-sb-input-select>
      </div>
    </div>
    }

    @if (defaultCampaignMissing) {
    <div class="alert alert-warning" [class.mt-4]="method === 'sso'">
      The default widget campaign is not set or no longer exists, so links without a campaign code will not open. Select one in the widget settings.
    </div>
    }

    <!-- The campaign in the link's path; left empty, the link opens the default widget campaign. -->
    <div class="mb-3" [class.mt-3]="method === 'sso'">
      <app-creatable-select
        labelText="Campaign"
        [hideInfo]="true"
        [isCreatable]="false"
        [isDisabled]="locked"
        [onlyBottomBorder]="false"
        [placeholder]="campaignPlaceholder"
        [selectedValue]="sampleCampaignId"
        [selectOptions]="campaignOptions"
        (optionSelected)="onSampleCampaignChange($event)"
      ></app-creatable-select>
    </div>

    @if (config.keyKinds.length) {
    <div class="mb-3">
      <app-sb-input-radio
        label="Key"
        layout="horizontal"
        ariaLabel="Key"
        [description]="keyDescription"
        [options]="keyOptions"
        [disabled]="locked"
        [ngModel]="keyKind"
        (ngModelChange)="onKeyKindChange($event)"
      ></app-sb-input-radio>
    </div>
    }

    @if (selectedSetting) {
    <div class="row">
      <div class="col-lg-6">
        <app-sb-copy-field
          label="Callback URL"
          help="Register this URL with the identity provider."
          [value]="selectedSetting.callback_url"
        ></app-sb-copy-field>
      </div>
      @if (selectedSetting.protocol === 'saml' && selectedSetting.metadata_url) {
      <div class="col-lg-6">
        <app-sb-copy-field
          label="SAML metadata URL"
          [value]="selectedSetting.metadata_url"
        ></app-sb-copy-field>
      </div>
      }
    </div>
    }

    <div [class.mt-4]="method !== 'email'">
      <app-sb-copy-field
        [label]="config.linkLabel"
        [description]="linkDescription"
        [value]="linkUrl"
      ></app-sb-copy-field>
    </div>
    @if (signingFailed) {
    <div class="bar error-brd sb-field__error">
      <div class="invalid-text">This browser cannot sign links on this page. Open the panel over https to see a signed sample.</div>
    </div>
    }

    @if (method === 'email') {
    <ul class="mt-3 mb-4 text-muted">
      <li>Replace {{sampleKey}} with the user's {{keyName}}@if (tokenPerEmail) {, and sign each link for that {{keyWord}} as described under How to sign}.</li>
      @if (keyKind === 'platformId') {
      <li>Set influencer_type to the user's influencer type, here the campaign's first segment's. The ID is signed with it, and the link opens that type's segment of the campaign.</li>
      }
      <li>The user should already be part of the campaign the link opens.</li>
      <li>A link for an unknown email or ID is refused. To show a default share page instead, add an influencer with the email defaultshare&#64;noemail.com, of the campaign's influencer type, and turn on the campaign's Generic Widget.</li>
    </ul>
    }

    <!-- While the method is off the block looks inactive, its controls disabled as the rest of the tab's. -->
    <div class="access-fields">
      <h5 class="mt-4 mb-2">Fields</h5>
      <div class="access-fields__info mb-3">
        <div class="sb-field__description">
          Tick a parameter to add it, with its sample value, to the {{config.linkLabel}}@if (config.signs) { and to the signing code}. @if (!config.signs) {With SSO the person's identity comes from the sign-in provider, so only the parameters SSO passes on are listed.}
          @if (signOptionAdds) {The sign option under Security adds {{signOptionAdds}} to it.}
        </div>
        @if (config.savedFields) {
        <div class="sb-field__description">{{config.savedFields}}</div>
        }
        @if (config.editModes) {
        <div class="sb-field__description">{{config.editModes}}</div>
        }
      </div>
      <!-- The drop-downs attach their panels to the body, so this scrolling wrapper cannot clip them. -->
      <div class="table-responsive">
        <table class="table table-sm access-fields-table">
          <thead>
            <tr>
              <th scope="col">Field</th>
              <th scope="col">Sample value</th>
              <th scope="col">Description</th>
              <th scope="col">URL parameter</th>
            </tr>
          </thead>
          <tbody>
            @for (field of visibleFieldOptions; track field) {
              <tr>
                <td class="access-fields-table__field">
                  <div class="access-fields-table__name">
                    <app-sb-input-checkbox
                      [id]="method + '-field-' + field.key"
                      [label]="field.label"
                      [disabled]="locked"
                      [ngModel]="fields[field.key].activated"
                      (ngModelChange)="onFieldToggle(field.key, $event)"
                    ></app-sb-input-checkbox>
                    @if (field.neededForNew) {
                    <span class="badge text-bg-light">Needed for new people</span>
                    }
                    @if (field.newPeopleOnly) {
                    <span class="badge text-bg-light">New people only</span>
                    }
                    @if (field.displayOnly) {
                    <span class="badge text-bg-light">Not saved</span>
                    }
                  </div>
                </td>
                <td><code class="access-fields-table__sample">{{field.sample}}</code></td>
                <td class="access-fields-table__description">{{field.description}}</td>
                <td><code class="access-fields-table__key">{{field.key}}</code></td>
              </tr>
              @if (keyNoteAfter(field.key); as note) {
              <tr class="access-fields-table__note">
                <td colspan="4">{{note}}</td>
              </tr>
              }
            }
            @if (config.targeting) {
              <tr class="access-fields-table__group">
                <td colspan="4">
                  Campaign targeting: left empty, a drop-down says what it uses instead. A segment of the campaign above replaces the influencer type.
                </td>
              </tr>
              @for (target of targetRows; track target) {
              <tr>
                <td class="access-fields-table__field">
                  <app-sb-input-checkbox
                    [id]="method + '-field-' + target.key"
                    [label]="target.label"
                    [ngModel]="fields[target.key].activated"
                    [disabled]="locked || targetBlocked(target.key)"
                    (ngModelChange)="onTargetToggle(target.key, $event)"
                  ></app-sb-input-checkbox>
                </td>
                <td class="access-fields-table__select">
                  <app-creatable-select
                    appendTo="body"
                    [isDisabled]="locked || targetBlocked(target.key) || !fields[target.key].activated"
                    [selectedValue]="fields[target.key].value"
                    [hideLabel]="true"
                    [isCreatable]="false"
                    [onlyBottomBorder]="false"
                    [placeholder]="targetPlaceholder(target.key)"
                    [selectOptions]="targetOptions(target.key)"
                    (optionSelected)="setFieldValue(target.key, $event)"
                  ></app-creatable-select>
                </td>
                <td class="access-fields-table__description">{{targetDescription(target)}}</td>
                <td><code class="access-fields-table__key">{{target.key}}</code></td>
              </tr>
              }
            }
          </tbody>
        </table>
      </div>
    </div>

    @if (config.signs) {
      <hr class="my-4">

      <app-share-widget-access-security
        [project]="project()"
        [method]="method"
        [locked]="locked"
        [saving]="saving"
        (stateChange)="onSecurityChange($event)"
      ></app-share-widget-access-security>

      <hr class="my-4">
      <h5 class="mb-3">{{howTo.heading}}</h5>
      @if (howTo.intro) {
      <div class="sb-field__description mb-3">{{howTo.intro}}</div>
      }
      @if (signMode === 'request_sign') {
      <pre class="access-signing-formula">{{signingFormula}}</pre>
      }
      @if (howTo.points.length) {
      <ul class="text-muted">
        @for (point of howTo.points; track point) {
        <li>{{point}}</li>
        }
      </ul>
      }
      <!-- One Node.js and one PHP field, showing the code that builds the link or the code that POSTs it. -->
      <div class="mb-3">
        <app-sb-input-radio
          label="Code"
          layout="horizontal"
          ariaLabel="Code"
          [options]="codeOptions"
          [disabled]="locked"
          [ngModel]="codeKind"
          (ngModelChange)="onCodeKindChange($event)"
        ></app-sb-input-radio>
      </div>
      <app-sb-copy-field
        class="access-code"
        label="Node.js"
        [multiline]="true"
        [rows]="codeRows(shownCode.node)"
        [value]="shownCode.node"
      ></app-sb-copy-field>
      <app-sb-copy-field
        class="access-code d-block mt-3"
        label="PHP"
        [multiline]="true"
        [rows]="codeRows(shownCode.php)"
        [value]="shownCode.php"
      ></app-sb-copy-field>
      @for (line of config.usage; track line) {
      <div class="sb-field__description mt-3">{{line}}</div>
      }
    }
  }

  <!-- Last in the tab, so it stays pinned above the app footer while the whole tab scrolls. It is
       always there, so a switch can be saved on an SSO tab without settings too. -->
  <div class="sb-sticky-actions mt-4">
    <app-sb-button
      variant="primary"
      icon="fa fa-check"
      label="Save"
      [loading]="saving"
      [disabled]="saving"
      (clicked)="save()"
    ></app-sb-button>
  </div>
</div>
